import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {compareDateStrings} from '../../utils/dateUtils';
import todayUtil from '../utils/today';
import {getMonthNamesInstance} from '../utils/MonthNames';
import DaySelection from '../DaySelection/DaySelection';
import dateUtils from '../utils/dateUtils';
import './Day.less';

class Day extends PureComponent {
    static propTypes = {
        day: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
        dayData: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
        dayIndex: PropTypes.number,
        dayTemplateComponent: PropTypes.func,
        hoverDate: PropTypes.string,
        languageTag: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        locale: PropTypes.string,
        monthNames: PropTypes.array, // eslint-disable-line react/forbid-prop-types
        onDayClick: PropTypes.func,
        onDisabledDayClick: PropTypes.func,
        onDayHover: PropTypes.func,
        selectionEnd: PropTypes.string,
        selectionStart: PropTypes.string,
        simple: PropTypes.bool,
        weekIndex: PropTypes.number
    };

    static defaultProps = {
        languageTag: {}
    };

    constructor(props) {
        super(props);

        this.state = {
            localeDateString: this.setLocaleDateString()
        };
    }

    setLocaleDateString = () => {
        const {day, languageTag} = this.props;
        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC'
        };
        const event = new Date(Date.UTC(day.year, day.month, day.day));
        const code = languageTag.code;

        return event.toLocaleDateString(code, options);
    };

    handleDayClick = (event) => {
        if (event) {
            event.stopPropagation();
        }
        const {day, dayData, dayTemplateComponent} = this.props;
        const today = todayUtil.getToday();
        const isToday = todayUtil.isToday(day, today);
        const isPast = todayUtil.isPast(day, today);
        const canClick =
            dayTemplateComponent && dayTemplateComponent.canClick
                ? dayTemplateComponent.canClick({dayData, day, isPast, isToday})
                : true;

        if (canClick) {
            this.props.onDayClick(day.dateString);
            this.props.setActiveMonth();
        } else {
            this.props.onDisabledDayClick(day.dateString);
        }
    };

    handleDayKeyboardEvents = (e) => {
        // Enter key or spacebar
        if (e.which === 13 || e.which === 32) {
            this.handleDayClick();
        }
    };

    handleDayHover = () => {
        const {day, dayData, dayTemplateComponent} = this.props;
        const today = todayUtil.getToday();
        const isToday = todayUtil.isToday(day, today);
        const isPast = todayUtil.isPast(day, today);
        const canSpan =
            dayTemplateComponent && dayTemplateComponent.canSpan
                ? dayTemplateComponent.canSpan({dayData, day, isPast, isToday})
                : true;

        if (canSpan) {
            this.props.onDayHover(this.props.day.dateString);
            this.props.setActiveMonth();
        }
    };

    mapMonthToAriaLabel = (dayObj) => {
        const monthNames =
            this.props.monthNames ||
            getMonthNamesInstance(this.props.locale).monthNames;

        return monthNames[dayObj.month];
    };

    buildDayTemplate(isToday, isPast, ariaLabelDate) {
        const {dayTemplateComponent, dayData, day} = this.props;
        const dateClasses = classNames({
            date: dayTemplateComponent ? false : true,
            today: isToday,
            past: isPast
        });

        if (dayTemplateComponent) {
            const DayTemplateComponent = dayTemplateComponent;
            return (
                <DayTemplateComponent
                    ariaLabelDate={ariaLabelDate}
                    dateString={day.dateString}
                    day={day.day}
                    dayData={dayData}
                    dayClasses={dateClasses}
                    isPast={isPast}
                    isToday={isToday}
                />
            );
        }

        return (
            <div className={dateClasses} aria-hidden="true">
                {day.day}
                <p className="hidden">{ariaLabelDate}</p>
            </div>
        );
    }

    getAriaLabel(day, date) {
        const {i18n} = this.props;
        const compare = compareDateStrings;
        const start = this.props.selectedDate || this.props.selectionStart;
        const end = this.props.selectionEnd;
        if (!day.day) {
            return '';
        } else if (start !== null && compare(start, day.dateString) === 0) {
            // Selected start date
            return i18n.ariaLabelCheckinSelected.replace('{0}', date);
        } else if (end !== null && compare(end, day.dateString) === 0) {
            // Selected end date
            return i18n.ariaLabelCheckoutSelected.replace('{0}', date);
        }
        return date;
    }

    render() {
        const {
            day,
            dayData,
            dayIndex,
            dayTemplateComponent,
            hoverDate,
            i18n,
            isFocusable,
            selectable,
            selectionEnd,
            selectedDate,
            selectionStart,
            setRef,
            simple,
            weekIndex
        } = this.props;
        const today = todayUtil.getToday();
        const isToday = todayUtil.isToday(day, today);
        const isPast = todayUtil.isPast(day, today);
        const lastDayOfWeek = (dayIndex + 1) % 7 === 0;
        const canSpan =
            dayTemplateComponent && dayTemplateComponent.canSpan
                ? dayTemplateComponent.canSpan({dayData, day, isPast, isToday})
                : true;
        const canClick =
            dayTemplateComponent && dayTemplateComponent.canClick
                ? dayTemplateComponent.canClick({dayData, day, isPast, isToday})
                : true;
        const ariaLabelDate = this.state.localeDateString;
        const dayTemplate = this.buildDayTemplate(
            isToday,
            isPast,
            ariaLabelDate
        );
        const isHidden = !day.day ? true : false;
        let isTouch = false;
        let tabIndex = -1;
        let ariaLabel = this.getAriaLabel(day, ariaLabelDate);

        /* istanbul ignore next */
        if (typeof window !== 'undefined') {
            isTouch = 'ontouchstart' in window;
        }

        const dayClasses = classNames({
            day: true,
            last: lastDayOfWeek,
            top: weekIndex === 0,
            empty: !day.day,
            'last-day': dateUtils.isLastDayInMonth(day),
            'first-day': dateUtils.isFirstDayInMonth(day),
            'no-gridlines': !this.props.showGridlines,
            past: isPast,
            selectable: selectable && canSpan,
            clickable: selectable && canClick
        });

        if (
            canSpan &&
            !canClick &&
            !!this.props.selectionStart &&
            !this.props.selectionEnd
        ) {
            ariaLabel = i18n.ariaLabelNotValidCheckout.replace(
                '{0}',
                ariaLabelDate
            );
        } else if (canSpan && !canClick) {
            ariaLabel = i18n.ariaLabelNotValidCheckin.replace(
                '{0}',
                ariaLabelDate
            );
        } else if (isPast || !canSpan || !canClick) {
            ariaLabel = i18n.ariaLabelUnavailable.replace('{0}', ariaLabelDate);
        }

        // Update the tabIndex of the cell depending on whether it is either
        // the selected start date, current day of the month, or the first day of the first visible month.
        if (
            day.dateString === selectionStart ||
            day.dateString === selectedDate
        ) {
            tabIndex = 0;
        } else if (isFocusable) {
            tabIndex = 0;
        } else if (!day.day || isPast) {
            tabIndex = -2; // A tabIndex of -2 is used to prevent navigation to empty cells and days in the past
        }

        return (
            <td
                className={dayClasses}
                ref={setRef}
                data-column={dayIndex}
                data-row={weekIndex}
                tabIndex={tabIndex}
                aria-label={ariaLabel}
                aria-hidden={isHidden}
                onMouseDown={isTouch ? null : this.handleDayClick}
                onClick={!isTouch ? null : this.handleDayClick}
                onFocus={isTouch ? null : this.handleDayHover}
                onKeyDown={this.handleDayKeyboardEvents}
                onMouseEnter={isTouch ? null : this.handleDayHover}
            >
                {/* Selection rendering */}
                <DaySelection
                    day={day}
                    selectionStart={selectionStart}
                    selectionEnd={selectionEnd}
                    selectedDate={selectedDate}
                    hoverDate={hoverDate}
                    simple={simple}
                    dayTemplate={dayTemplate}
                    ariaLabelDate={ariaLabelDate}
                />
            </td>
        );
    }
}

export default Day;
